import {
  Archive,
  FlaskConical,
  Globe,
  Languages,
  List,
  type LucideIcon,
  Mic,
  PackageCheck,
  PackageOpen,
  Pencil,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Undo2,
  Upload,
} from "lucide-react";
import type { AgeRating } from "./ds/icons/AgeRatingBadge";

export type Phase = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type AccessRole = "manager" | "writer" | "voice_actor" | "reader";
export type UserRole = "owner" | AccessRole;

export interface SeriesAssignmentEntry {
  pk: number;
  user: { id: number; username: string };
  role: AccessRole;
}

export interface SeriesInvitationEntry {
  pk: number;
  role: AccessRole;
  expiresAt: string;
  createdBy: { id: number; username: string } | null;
  token: string;
}

export interface SharingInfo {
  owner: { id: number; username: string };
  assignments: SeriesAssignmentEntry[];
  invitations: SeriesInvitationEntry[];
}

export const phases: { id: Phase; label: string; icon: LucideIcon }[] = [
  { id: 0, label: "Writing", icon: Pencil },
  { id: 1, label: "Testing and Editing", icon: FlaskConical },
  { id: 2, label: "Translating", icon: Languages },
  { id: 3, label: "Recording & Sound Design", icon: Mic },
  { id: 4, label: "Final testing", icon: ShieldCheck },
  { id: 5, label: "Ready for release", icon: PackageCheck },
  { id: 6, label: "Available on the stores", icon: Globe },
];

export interface RegistryEpisode {
  pk: number;
  uid?: string;
  label: string;
  weblateLink: string | null;
  seriesTitle: string;
  seriesUserRole: UserRole;
  series: number;
  phase?: Phase;
  cover?: string;
  /** Shown while the episode plays. Ships inside the bundle, not the catalog. */
  playerImage?: string;
  summary?: string;
  /** Derived from the episode position; 0 for the pilot. Never client-set. */
  number?: number | null;
  isPilot?: boolean;
}

export interface RegistrySeries {
  pk: number;
  title: string;
  cover?: string;
  description?: string;
  category?: number;
  episodeSet: RegistryEpisode[];
  genre?: {
    id: number;
    name: string;
  };
  tags?: string[];
  ageRestriction?: AgeRating;
  credits?: string;
  owner?: {
    id: number;
    username: string;
  };
  modified?: string;
  phase?: Phase;
  userRole?: UserRole;
  sensitiveContent?: boolean;
  artwork?: string;
  titleArtwork?: string;
  /** The season's main theme, an mp3. Reaches players on the next publish. */
  audio?: string;
  secondaryGenres?: {
    id: number;
    name: string;
  }[];
}

export interface RegistryGenre {
  id: number;
  name: string;
}

export interface RegistryCategory {
  id: number;
  name: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type SortBy = "title" | "-modified" | "phase";

export interface RegistryFilters {
  categoryId?: number;
  genreId?: number;
  query?: string;
  sortBy?: SortBy;
}

export type TaskName =
  | "export-script-for-actors"
  | "export-i18n-to-weblate"
  | "generate-package"
  | "generate-json"
  | "upload-mobile-package"
  | "bulk-upload-assets"
  | "build-bundle"
  // Side effects of the Publishing page, never launched from the task dropdown.
  | "publish-bundle"
  | "unpublish-bundle";

export interface TaskConfigInst {
  name: TaskName;
  // Extra field values for this task invocation, sent as individual top-level fields
  // (matching the task's dedicated Req<Task>Params serializer in books/serializers.py)
  // rather than as one nested JSON blob.
  values: Record<string, unknown>;
}

export type TaskParamFieldType =
  | "text"
  | "number"
  | "boolean"
  | "select"
  | "file";

export interface TaskParamOption {
  value: string;
  label: string;
}

export interface TaskParamField {
  name: string;
  label: string;
  type: TaskParamFieldType;
  required?: boolean;
  helpText?: string;
  accept?: string;
  options?: TaskParamOption[];
}

// Per-invocation overrides for a task's params schema (e.g. a select field whose
// options/default depend on the page the task is triggered from, not on the task itself).
export interface TaskParamOverride {
  value?: unknown;
  options?: TaskParamOption[];
}

export type TaskParamOverrides = Record<string, TaskParamOverride>;

export interface TaskConfigCustom extends TaskConfigInst {
  label: string;
  paramOverrides?: TaskParamOverrides;
}

interface TaskConfig {
  name: TaskName;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  argsRequired?: true;
  // Keep in sync with USER_ALLOWED_TASKS in books/views.py
  superuserOnly?: true;
  // Started by the app itself (publishing), never offered in the task dropdown. Listed here
  // only so the task history can show a name instead of the raw slug.
  internal?: true;
  // Schema used to render a form asking the user for extra task parameters.
  // Keep in sync with the corresponding Req<Task>Params serializer in books/serializers.py
  params?: TaskParamField[];
}

export const availableTasks: TaskConfig[] = [
  {
    name: "export-i18n-to-weblate",
    label: "Sync translations",
    description:
      "Exports all translatable strings from this episode to Weblate, making them available for the translation team.",
    icon: Languages,
    enabled: true,
    superuserOnly: true,
  },
  {
    name: "export-script-for-actors",
    label: "Export script for actors",
    description:
      "Generates a formatted script document with all actor lines, stage directions, and audio cues — ready to share with the recording team.",
    icon: List,
    enabled: true,
  },
  {
    name: "generate-json",
    label: "Generate all JSONs (internal use ONLY)",
    description:
      "Generates the bundle's JSON files from the current episode content. The files contain internal information, and should not be shared externally.",
    icon: SlidersHorizontal,
    enabled: true,
    superuserOnly: true,
  },
  {
    name: "generate-package",
    label: "Generate bundle (deprecated)",
    description:
      "Packages all episode assets — audio, images, and game data — into a distributable bundle for the mobile app. Deprecated: use “Build bundle” (or the Publishing page), which tracks the bundle for publishing.",
    icon: Archive,
    enabled: true,
    superuserOnly: true,
  },
  {
    name: "build-bundle",
    label: "Build bundle",
    description:
      "Packages all episode assets — audio, images, and game data — into a bundle ready to be published from the Publishing page.",
    icon: PackageCheck,
    enabled: true,
  },
  {
    name: "upload-mobile-package",
    label: "Upload mobile package",
    description:
      "Uploads the previously generated package to the mobile app distribution server.",
    icon: Upload,
    enabled: true,
    argsRequired: true,
    superuserOnly: true,
  },
  {
    name: "bulk-upload-assets",
    label: "Bulk upload assets",
    description:
      "Uploads a zip archive of recorded audio and image files, matching each one to the episode's expected assets by filename.",
    icon: PackageOpen,
    enabled: true,
    argsRequired: true,
    params: [
      {
        name: "archive",
        label: "Zip archive",
        type: "file",
        required: true,
        accept: ".zip,application/zip",
      },
      {
        name: "language",
        label: "Language",
        type: "select",
        // [sp] this list is overridden when executed by an user from the post-prod UI
        options: [
          { label: "EN", value: "en" },
          { label: "IT", value: "it" },
          { label: "PT", value: "pt" },
        ],
        required: true,
      },
    ],
  },
  {
    name: "publish-bundle",
    label: "Publish episode",
    description:
      "Uploads a bundle to the store, refreshes the catalog, and makes the episode reachable — hidden behind a deep-link for a beta, listed for everyone for an official release.",
    icon: Rocket,
    enabled: true,
    internal: true,
  },
  {
    name: "unpublish-bundle",
    label: "Un-publish episode",
    description:
      "Withdraws a publication and refreshes the catalog. Nothing is deleted: the story goes back to being hidden, and testers holding the deep-link can still open it.",
    icon: Undo2,
    enabled: true,
    internal: true,
  },
] as const;
